import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Truck, RotateCcw, AlertTriangle, Clock, CalendarRange,
  ChevronDown, ChevronRight, Search, X, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { useSociete } from '../context/SocieteContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return '—';
  const [y, m, day] = d.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function diffJours(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const target = new Date(y, m - 1, d);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((today - target) / 86400000); // positif = retard
}

// ─── Configuration ────────────────────────────────────────────────────────────

const PERIODES = {
  en_retard:   { label: 'En retard',   color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', icon: AlertTriangle },
  aujourd_hui: { label: "Aujourd'hui", color: '#D97706', bg: '#FFFBEB', border: '#FDE68A', icon: Clock },
  a_venir:     { label: 'À venir',     color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE', icon: CalendarRange },
};

const FILTER_CARDS = [
  { id: 'sorties_retard',  label: 'Sorties en retard', icon: Truck,      bg: '#FEF2F2', border: '#FECACA', color: '#DC2626', countKey: 'sorties_retard',  tab: 'sorties', periode: 'en_retard'   },
  { id: 'sorties_jour',    label: 'Sorties du jour',   icon: Truck,      bg: '#FFFBEB', border: '#FDE68A', color: '#D97706', countKey: 'sorties_jour',    tab: 'sorties', periode: 'aujourd_hui' },
  { id: 'retours_retard',  label: 'Retours en retard', icon: RotateCcw,  bg: '#FDF4FF', border: '#E9D5FF', color: '#7C3AED', countKey: 'retours_retard',  tab: 'retours', periode: 'en_retard'   },
  { id: 'retours_jour',    label: 'Retours du jour',   icon: RotateCcw,  bg: '#F0FDF4', border: '#BBF7D0', color: '#15803D', countKey: 'retours_jour',    tab: 'retours', periode: 'aujourd_hui' },
];

// ─── Aplatissement des données ────────────────────────────────────────────────

function flattenData(groups, type) {
  const rows = [];
  for (const client of groups) {
    for (const contrat of client.contrats) {
      for (const ligne of contrat.lignes) {
        rows.push({
          key:             ligne.ligne_id,
          categorie_nom:   ligne.categorie_nom    || '—',
          gamme_nom:       ligne.gamme_nom        || '',
          materiel_marque: ligne.materiel_marque  || '',
          materiel_modele: ligne.materiel_modele  || '',
          materiel_parc:   ligne.materiel_parc    || '',
          date_ref:        type === 'sortie' ? ligne.date_debut : ligne.date_fin,
          periode:         ligne.periode,
          contrat_id:      contrat.contrat_id,
          numero:          contrat.numero         || '—',
          statut:          contrat.statut,
          client_nom:      client.client_nom      || '—',
        });
      }
    }
  }
  return rows;
}

// ─── Ligne plate ──────────────────────────────────────────────────────────────

function LigneRow({ row, type, onNavigate }) {
  const retardJours  = row.periode === 'en_retard' ? diffJours(row.date_ref) : null;
  const isRetard     = row.periode === 'en_retard';
  const isAujourdhui = row.periode === 'aujourd_hui';

  const col = { fontSize: '0.8125rem', color: 'var(--text-secondary)', fontWeight: 400, lineHeight: '1.4' };

  return (
    <tr
      onClick={() => onNavigate(row.contrat_id)}
      data-testid={`livraison-row-${row.key}`}
      style={{ cursor: 'pointer', borderBottom: '1px solid var(--border-light)' }}
      className="hover:bg-blue-50 transition-colors"
    >
      {/* Contrat */}
      <td className="px-4 py-3" style={{ ...col, fontWeight: 500 }}>{row.numero}</td>

      {/* Client */}
      <td className="px-4 py-3" style={col}>{row.client_nom}</td>

      {/* Catégorie · Gamme */}
      <td className="px-4 py-3" style={col}>
        {[row.categorie_nom, row.gamme_nom].filter(v => v && v !== '—').join(' ') || '—'}
      </td>

      {/* Marque · Modèle */}
      <td className="px-4 py-3" style={col}>
        {[row.materiel_marque, row.materiel_modele].filter(Boolean).join(' ') || '—'}
      </td>

      {/* N° Parc */}
      <td className="px-4 py-3" style={col}>
        {row.materiel_parc ? `N°${row.materiel_parc}` : '—'}
      </td>

      {/* Date prévue — jamais rouge */}
      <td className="px-4 py-3" style={col}>{fmtDate(row.date_ref)}</td>

      {/* Jours de retard — rouge uniquement ici */}
      <td className="px-4 py-3 text-center" style={{ minWidth: 110 }}>
        {isRetard && retardJours !== null && retardJours > 0
          ? <span style={{ ...col, color: '#DC2626', fontWeight: 600 }}>{retardJours} j</span>
          : isAujourdhui
          ? <span style={{ ...col, color: '#D97706' }}>Aujourd'hui</span>
          : ''}
      </td>
    </tr>
  );
}

// ─── Section période ──────────────────────────────────────────────────────────

function PeriodeSection({ periode, rows, type, onNavigate }) {
  const [open, setOpen] = useState(true);
  const cfg = PERIODES[periode];
  const Icon = cfg.icon;

  if (!rows.length) return null;

  return (
    <>
      {/* Séparateur titre */}
      <tr data-testid={`livraison-section-${periode}`}>
        <td colSpan={6} style={{ padding: '4px 16px 0' }}>
          <button
            onClick={() => setOpen(v => !v)}
            className="flex items-center gap-2 py-2 w-full text-left"
          >
            <span
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
              style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
            >
              <Icon size={11} />
              {cfg.label}
              <span
                className="px-1.5 py-0.5 rounded-full text-xs font-bold"
                style={{ background: cfg.color, color: 'white' }}
              >
                {rows.length}
              </span>
            </span>
            <span style={{ color: 'var(--text-muted)' }}>
              {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            </span>
          </button>
        </td>
      </tr>

      {/* Lignes */}
      {open && rows.map(row => (
        <LigneRow key={row.key} row={row} type={type} onNavigate={onNavigate} />
      ))}
    </>
  );
}

// ─── Contenu d'un onglet ──────────────────────────────────────────────────────

function TabContent({ data, type, filterPeriode, aVenirDays, onNavigate }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);

  // Aplatir toutes les lignes
  let allRows = flattenData(data, type);
  // Filtre horizon "À venir"
  if (aVenirDays) {
    const limit = new Date(today);
    limit.setDate(limit.getDate() + aVenirDays);
    allRows = allRows.filter(r => {
      if (r.periode !== 'a_venir') return true; // garder retard + aujourd_hui
      if (!r.date_ref) return false;
      const [y, m, d] = r.date_ref.split('-').map(Number);
      return new Date(y, m - 1, d) <= limit;
    });
  }

  const periodes = filterPeriode ? [filterPeriode] : ['en_retard', 'aujourd_hui', 'a_venir'];

  const sectionRows = periodes.reduce((acc, p) => {
    acc[p] = allRows.filter(r => r.periode === p);
    return acc;
  }, {});

  const hasContent = periodes.some(p => sectionRows[p]?.length > 0);

  if (!hasContent) {
    return (
      <tr>
        <td colSpan={6}>
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
              style={{ background: 'var(--bg-subtle)' }}>
              {type === 'sortie'
                ? <Truck size={22} style={{ color: 'var(--text-muted)' }} />
                : <RotateCcw size={22} style={{ color: 'var(--text-muted)' }} />}
            </div>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Aucune {type === 'sortie' ? 'sortie' : 'retour'} pour cette sélection
            </p>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <>
      {periodes.map(p => (
        <PeriodeSection
          key={p}
          periode={p}
          rows={sectionRows[p] || []}
          type={type}
          onNavigate={onNavigate}
        />
      ))}
    </>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function Livraisons() {
  const navigate = useNavigate();
  const { activeAgences } = useSociete();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [activeTab, setActiveTab] = useState('sorties');
  const [filterPeriode, setFilterPeriode] = useState(null);
  const [aVenirDays, setAVenirDays] = useState(30);
  const [showAVenir, setShowAVenir] = useState(false);
  const searchTimer = useRef(null);
  const lastQuery = useRef('');

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const res = await api.getLivraisons(q ? { q } : {});
      setData(res.data);
    } catch {
      toast.error('Erreur lors du chargement des livraisons');
    } finally {
      setLoading(false);
    }
  }, [activeAgences]);

  useEffect(() => { load(); }, [load]);

  const handleSearchChange = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      if (val !== lastQuery.current) {
        lastQuery.current = val;
        load(val);
      }
    }, 400);
  };

  const handleCardClick = (card) => {
    const sameCard = activeTab === card.tab && filterPeriode === card.periode;
    setActiveTab(card.tab);
    setFilterPeriode(sameCard ? null : card.periode);
  };

  // Navigation vers le contrat ouvert
  const handleNavigate = (contratId) => {
    navigate(`/contrats?contrat_id=${contratId}`);
  };

  const counts = data?.counts || {};
  const sorties = data?.sorties || [];
  const retours = data?.retours || [];

  const totalSorties = flattenData(sorties, 'sortie').length;
  const totalRetours = flattenData(retours, 'retour').length;

  return (
    <div className="h-full flex flex-col" style={{ background: 'var(--bg-subtle)' }}>
      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between flex-shrink-0"
        style={{ background: 'white', borderBottom: '1px solid var(--border)' }}>
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--text-secondary)' }}>Livraisons</h1>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Suivi des sorties et retours de matériels</p>
        </div>
        <button
          onClick={() => load(lastQuery.current)}
          disabled={loading}
          data-testid="livraisons-refresh-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium hover:opacity-80"
          style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

        {/* 4 Cards filtrantes */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {FILTER_CARDS.map(card => {
            const Icon = card.icon;
            const count = counts[card.countKey] || 0;
            const isActive = activeTab === card.tab && filterPeriode === card.periode;
            return (
              <button
                key={card.id}
                onClick={() => handleCardClick(card)}
                data-testid={`livraison-card-${card.id}`}
                className="rounded-xl p-4 text-left transition-all hover:shadow-md active:scale-[0.98]"
                style={{
                  background: isActive ? card.bg : 'white',
                  border: `2px solid ${isActive ? card.color : 'var(--border)'}`,
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: card.bg }}>
                    <Icon size={16} style={{ color: card.color }} />
                  </div>
                  <span className="text-2xl font-black" style={{ color: count > 0 ? card.color : 'var(--text-muted)' }}>
                    {loading ? '—' : count}
                  </span>
                </div>
                <p className="mt-2.5 text-xs font-semibold" style={{ color: card.color }}>{card.label}</p>
              </button>
            );
          })}
        </div>

        {/* Barre recherche + filtres */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
            <input
              type="text"
              placeholder="Rechercher un client, contrat, gamme..."
              value={searchInput}
              onChange={e => handleSearchChange(e.target.value)}
              className="w-full pl-9 pr-8 py-2 rounded-lg text-sm border bg-white"
              style={{ borderColor: 'var(--border)', outline: 'none', color: 'var(--text-secondary)' }}
              data-testid="livraisons-search-input"
            />
            {searchInput && (
              <button onClick={() => handleSearchChange('')} className="absolute right-2.5 top-1/2 -translate-y-1/2">
                <X size={13} style={{ color: 'var(--text-muted)' }} />
              </button>
            )}
          </div>

          <button
            onClick={() => setShowAVenir(v => !v)}
            data-testid="livraisons-avenir-toggle"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border transition-all"
            style={{
              borderColor: showAVenir ? 'var(--primary-solid)' : 'var(--border)',
              color: showAVenir ? 'var(--primary-solid)' : 'var(--text-muted)',
              background: showAVenir ? 'var(--primary-50)' : 'white',
            }}
          >
            <CalendarRange size={13} />
            À venir
          </button>

          {showAVenir && (
            <select
              value={aVenirDays}
              onChange={e => setAVenirDays(Number(e.target.value))}
              data-testid="livraisons-avenir-horizon"
              className="px-2.5 py-2 rounded-lg text-sm border"
              style={{ borderColor: 'var(--primary-solid)', color: 'var(--primary-solid)', background: 'var(--primary-50)' }}
            >
              <option value={7}>7 prochains jours</option>
              <option value={14}>14 prochains jours</option>
              <option value={30}>30 prochains jours</option>
              <option value={90}>3 prochains mois</option>
              <option value={0}>Tout afficher</option>
            </select>
          )}

          {filterPeriode && (
            <button
              onClick={() => setFilterPeriode(null)}
              data-testid="livraisons-reset-filter"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border hover:opacity-80"
              style={{ borderColor: 'var(--border)', color: 'var(--text-muted)', background: 'white' }}
            >
              <X size={13} />
              Effacer filtre
            </button>
          )}
        </div>

        {/* Tableau */}
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)', background: 'white' }}>

          {/* Onglets */}
          <div className="flex" style={{ borderBottom: '1px solid var(--border)' }}>
            {[
              { key: 'sorties', label: 'Sorties', icon: Truck,     count: totalSorties },
              { key: 'retours', label: 'Retours', icon: RotateCcw, count: totalRetours },
            ].map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => { setActiveTab(tab.key); setFilterPeriode(null); }}
                  data-testid={`livraisons-tab-${tab.key}`}
                  className="flex items-center gap-2 px-6 py-3.5 text-sm font-semibold transition-colors"
                  style={{
                    color:        isActive ? 'var(--primary-solid)' : 'var(--text-muted)',
                    borderBottom: isActive ? '2px solid var(--primary-solid)' : '2px solid transparent',
                    background:   isActive ? 'var(--primary-50)' : 'transparent',
                    marginBottom: -1,
                  }}
                >
                  <Icon size={14} />
                  {tab.label}
                  {!loading && tab.count > 0 && (
                    <span
                      className="px-1.5 py-0.5 rounded-full text-xs font-bold"
                      style={{
                        background: isActive ? 'var(--primary-solid)' : 'var(--bg-subtle)',
                        color:      isActive ? 'white' : 'var(--text-muted)',
                      }}
                    >
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* En-têtes colonnes */}
          <table className="w-full table-fixed">
            <colgroup>
              <col style={{ width: '11%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '17%' }} />
              <col style={{ width: '9%'  }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '12%' }} />
            </colgroup>
            <thead style={{ borderBottom: '1px solid var(--border)' }}>
              <tr style={{ background: 'var(--bg-subtle)' }}>
                {['Contrat', 'Client', 'Catégorie / Gamme', 'Marque / Modèle', 'N° Parc',
                  activeTab === 'sorties' ? 'Date sortie prévue' : 'Date retour prévue',
                  'Retard'].map((h, i) => (
                  <th key={h} className={`px-4 py-2.5 ${i === 6 ? 'text-center' : 'text-left'}`}
                    style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6}>
                    <div className="flex items-center justify-center py-16">
                      <div className="w-7 h-7 rounded-full border-2 border-t-transparent animate-spin"
                        style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
                    </div>
                  </td>
                </tr>
              ) : (
                <TabContent
                  data={activeTab === 'sorties' ? sorties : retours}
                  type={activeTab === 'sorties' ? 'sortie' : 'retour'}
                  filterPeriode={filterPeriode}
                  aVenirDays={showAVenir ? (aVenirDays || null) : null}
                  onNavigate={handleNavigate}
                />
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
